// src/pages/ComptableDashboard.jsx
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { saleInvoicesAPI, purchaseInvoicesAPI } from '../services/api';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  Calculator, Receipt, ShoppingCart, FolderKanban, UsersRound,
  RefreshCw, ArrowRight, CheckCircle2,
  TrendingUp, Timer, ReceiptText,
} from 'lucide-react';

const MONTHS_FR = ['Janvier','Février','Mars','Avril','Mai','Juin',
                   'Juillet','Août','Septembre','Octobre','Novembre','Décembre'];
const DAYS_FR   = ['Dimanche','Lundi','Mardi','Mercredi','Jeudi','Vendredi','Samedi'];

function fmtDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  return `${String(d.getDate()).padStart(2,'0')}/${String(d.getMonth()+1).padStart(2,'0')}/${d.getFullYear()}`;
}

function fmtAmount(val, devise = 'EUR') {
  if (val == null) return '—';
  return Number(val).toLocaleString('fr-FR', { style: 'currency', currency: devise || 'EUR', maximumFractionDigits: 0 });
}

const STATUT_SALE = {
  NEW:        { label: 'Nouveau',    color: '#6B7280', bg: 'rgba(107,114,128,0.10)' },
  EN_ATTENTE: { label: 'En attente', color: '#F59E0B', bg: 'rgba(245,158,11,0.10)' },
  VALIDEE:    { label: 'Validée',    color: '#3B82F6', bg: 'rgba(59,130,246,0.10)' },
  PAYEE:      { label: 'Payée',      color: '#16A34A', bg: 'rgba(34,197,94,0.10)' },
  ANNULEE:    { label: 'Annulée',    color: '#EF4444', bg: 'rgba(239,68,68,0.10)' },
};

function StatutBadge({ statut, meta }) {
  const m = meta[statut] || { label: statut, color: '#6B7280', bg: 'rgba(107,114,128,0.10)' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center',
      padding: '2px 9px', borderRadius: 10,
      fontSize: '0.72rem', fontWeight: 700,
      color: m.color, background: m.bg,
      border: `1px solid ${m.color}33`, whiteSpace: 'nowrap',
    }}>{m.label}</span>
  );
}

function KpiCard({ icon: Icon, label, value, sub, color, onClick }) {
  return (
    <div onClick={onClick} style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border)',
      borderRadius: 12,
      padding: '18px 20px',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'box-shadow 0.15s, border-color 0.15s',
      display: 'flex', flexDirection: 'column', gap: 8,
    }}
    onMouseEnter={e => { if (onClick) { e.currentTarget.style.boxShadow = 'var(--shadow-md)'; e.currentTarget.style.borderColor = color; } }}
    onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div style={{
          width: 38, height: 38, borderRadius: 9,
          background: `${color}18`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <Icon size={18} color={color} />
        </div>
        {onClick && <ArrowRight size={14} color="var(--text-faint)" />}
      </div>
      <div>
        <div style={{ fontSize: '1.75rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1.1 }}>
          {value}
        </div>
        <div style={{ fontSize: '0.8125rem', fontWeight: 600, color: 'var(--text-muted)', marginTop: 2 }}>
          {label}
        </div>
        {sub && <div style={{ fontSize: '0.75rem', color: 'var(--text-faint)', marginTop: 2 }}>{sub}</div>}
      </div>
    </div>
  );
}

function QuickLink({ icon: Icon, label, to, color, navigate }) {
  return (
    <button onClick={() => navigate(to)} style={{
      background: 'var(--bg-elevated)',
      border: `1px solid ${color}33`,
      borderRadius: 10,
      padding: '12px 16px',
      cursor: 'pointer',
      display: 'flex', alignItems: 'center', gap: 10,
      color: 'var(--text-primary)',
      transition: 'background 0.15s, box-shadow 0.15s',
      textAlign: 'left',
    }}
    onMouseEnter={e => { e.currentTarget.style.background = `${color}10`; }}
    onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-elevated)'; }}
    >
      <div style={{
        width: 32, height: 32, borderRadius: 8,
        background: `${color}18`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
      }}>
        <Icon size={16} color={color} />
      </div>
      <span style={{ fontSize: '0.875rem', fontWeight: 600 }}>{label}</span>
      <ArrowRight size={13} color={color} style={{ marginLeft: 'auto' }} />
    </button>
  );
}

export default function ComptableDashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const [loading,         setLoading]         = useState(true);
  const [saleInvoices,    setSaleInvoices]    = useState([]);
  const [purchaseInvoices,setPurchaseInvoices] = useState([]);
  const [notesFrais,      setNotesFrais]      = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [saleRes, buyRes, nfRes] = await Promise.allSettled([
        saleInvoicesAPI.list({ limit: 50 }),
        purchaseInvoicesAPI.list({ limit: 50 }),
        api.get('/notes-frais/', { params: { limit: 50 } }),
      ]);
      if (saleRes.status === 'fulfilled') setSaleInvoices(saleRes.value.data?.items || []);
      if (buyRes.status  === 'fulfilled') setPurchaseInvoices(buyRes.value.data?.items || []);
      if (nfRes.status   === 'fulfilled') setNotesFrais(Array.isArray(nfRes.value.data) ? nfRes.value.data : (nfRes.value.data?.items || []));
    } catch {
      toast.error('Erreur chargement données');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const now     = new Date();
  const hour    = now.getHours();
  const greeting = hour < 12 ? 'Bonjour' : hour < 18 ? 'Bon après-midi' : 'Bonsoir';

  // Stats
  const saleNew    = saleInvoices.filter(i => i.statut === 'NEW' || i.statut === 'EN_ATTENTE').length;
  const salePayees = saleInvoices.filter(i => i.statut === 'PAYEE').length;
  const saleTotalHT = saleInvoices.reduce((s, i) => s + Number(i.total_ht || 0), 0);
  const buyPending = purchaseInvoices.filter(i => i.statut === 'EN_ATTENTE').length;
  const nfPending  = notesFrais.filter(n => n.statut === 'SOUMIS').length;

  const recentSale = [...saleInvoices]
    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
    .slice(0, 8);

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-main)', paddingBottom: 40 }}>

      {/* ── Hero ──────────────────────────────────────────────── */}
      <div style={{
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 55%, #3730a3 100%)',
        padding: '32px 36px 28px',
        position: 'relative', overflow: 'hidden',
        marginBottom: 28,
      }}>
        <div style={{ position: 'absolute', top: -30, right: -30, width: 180, height: 180,
                      borderRadius: '50%', background: 'rgba(255,255,255,0.04)', pointerEvents: 'none' }}/>
        <div style={{ position: 'absolute', bottom: -40, right: 120, width: 120, height: 120,
                      borderRadius: '50%', background: 'rgba(255,255,255,0.03)', pointerEvents: 'none' }}/>

        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', position: 'relative' }}>
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <div style={{ width: 40, height: 40, borderRadius: '50%',
                            background: 'rgba(165,180,252,0.18)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Calculator size={20} color="#a5b4fc" />
              </div>
              <div>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: '#a5b4fc',
                              textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                  Comptabilité
                </div>
                <div style={{ fontSize: '1.6rem', fontWeight: 800, color: '#ffffff', lineHeight: 1.1 }}>
                  {greeting}, {user?.prenom || user?.nom || 'Comptable'} !
                </div>
              </div>
            </div>
            <div style={{ marginLeft: 50, marginTop: 4, color: 'rgba(165,180,252,0.8)', fontSize: '0.875rem' }}>
              {DAYS_FR[now.getDay()]} {now.getDate()} {MONTHS_FR[now.getMonth()]} {now.getFullYear()}
            </div>
          </div>
          <button onClick={fetchData} disabled={loading} style={{
            background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.2)',
            borderRadius: 8, padding: '8px 14px', color: '#c7d2fe',
            cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6,
            fontSize: '0.8125rem', fontWeight: 600,
          }}>
            <RefreshCw size={14} className={loading ? 'spin' : ''}/> Actualiser
          </button>
        </div>
      </div>

      <div style={{ padding: '0 32px' }}>

        {/* ── KPI Cards ─────────────────────────────────────── */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 14, marginBottom: 28 }}>
          <KpiCard icon={Receipt}      label="Factures de vente à traiter" value={saleNew}    color="#3B82F6" onClick={() => navigate('/sale-invoices')} />
          <KpiCard icon={ShoppingCart} label="Factures d'achat en attente"  value={buyPending} color="#F59E0B" onClick={() => navigate('/purchase-invoices')} />
          <KpiCard icon={ReceiptText}  label="Notes de frais soumises"       value={nfPending}  color="#8B5CF6" onClick={() => navigate('/notes-frais')} />
          <KpiCard icon={CheckCircle2} label="Factures vente payées"          value={salePayees} color="#16A34A" />
          <KpiCard icon={TrendingUp}   label="CA total HT (portefeuille)"
            value={loading ? '…' : Number(saleTotalHT).toLocaleString('fr-FR', { maximumFractionDigits: 0 }) + ' €'}
            color="#06B6D4" />
        </div>

        {/* ── Raccourcis ────────────────────────────────────── */}
        <div style={{ marginBottom: 28 }}>
          <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)',
                        textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 12 }}>
            Accès rapides
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 10 }}>
            <QuickLink icon={Receipt}      label="Factures de vente"    to="/sale-invoices"     color="#3B82F6" navigate={navigate} />
            <QuickLink icon={ShoppingCart} label="Factures d'achat"     to="/purchase-invoices" color="#F59E0B" navigate={navigate} />
            <QuickLink icon={FolderKanban} label="Projets"              to="/projects"          color="#06B6D4" navigate={navigate} />
            <QuickLink icon={UsersRound}   label="Ressources"           to="/resources"         color="#16A34A" navigate={navigate} />
            <QuickLink icon={Timer}        label="Feuilles de temps"    to="/timesheets/history" color="#8B5CF6" navigate={navigate} />
            <QuickLink icon={ReceiptText}  label="Notes de frais"       to="/notes-frais"       color="#EC4899" navigate={navigate} />
          </div>
        </div>

        {/* ── Factures de vente récentes ────────────────────── */}
        <div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
            <div style={{ fontSize: '0.8125rem', fontWeight: 700, color: 'var(--text-muted)',
                          textTransform: 'uppercase', letterSpacing: '0.06em' }}>
              Factures de vente récentes
            </div>
            <button onClick={() => navigate('/sale-invoices')} style={{
              background: 'none', border: 'none', color: '#3B82F6',
              fontSize: '0.8125rem', fontWeight: 600, cursor: 'pointer',
              display: 'flex', alignItems: 'center', gap: 4,
            }}>
              Voir tout <ArrowRight size={13}/>
            </button>
          </div>

          {loading ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)' }}>Chargement…</div>
          ) : recentSale.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              Aucune facture de vente
            </div>
          ) : (
            <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 10, overflow: 'hidden' }}>
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8125rem' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border)', background: 'var(--bg-secondary)' }}>
                      {['Réf.', 'Client', 'Date', 'Montant HT', 'Statut'].map(h => (
                        <th key={h} style={{ padding: '9px 14px', textAlign: 'left', fontWeight: 700,
                                             color: 'var(--text-muted)', fontSize: '0.75rem',
                                             textTransform: 'uppercase', letterSpacing: '0.04em' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {recentSale.map((inv, i) => (
                      <tr key={inv.id}
                        onClick={() => navigate(`/sale-invoices/${inv.id}`)}
                        style={{
                          cursor: 'pointer',
                          borderBottom: i < recentSale.length - 1 ? '1px solid var(--border)' : 'none',
                          transition: 'background 0.1s',
                        }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-secondary)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                      >
                        <td style={{ padding: '10px 14px', fontWeight: 600, color: '#3B82F6' }}>{inv.ref || '—'}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-primary)' }}>{inv.destinataire || '—'}</td>
                        <td style={{ padding: '10px 14px', color: 'var(--text-muted)' }}>{fmtDate(inv.date_facture)}</td>
                        <td style={{ padding: '10px 14px', fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                          {fmtAmount(inv.total_ht, inv.currency || inv.devise)}
                        </td>
                        <td style={{ padding: '10px 14px' }}>
                          <StatutBadge statut={inv.statut} meta={STATUT_SALE} />
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

      </div>
    </div>
  );
}
